import { useState, useEffect } from 'react';
import { Music } from 'lucide-react';

/**
 * <img> wrapper that swaps in a graceful fallback when the source fails
 * to load (e.g., a dangling presigned R2 URL returning 403/404). Avoids
 * the browser's ugly broken-image glyph that leaked through the
 * Library / Set rows / mini-player / cover stacks when production R2
 * lost cover files.
 *
 * Usage:
 *   <SafeImg src={track.cover_url} alt="" className="…object-cover" />
 *
 * Falls back to <Music> inside a centred slot of the same size. Resets
 * the error flag whenever `src` changes so a fresh URL gets a real try.
 */
export default function SafeImg({
  src,
  alt = '',
  className = '',
  fallbackClassName = 'w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF]',
  iconClassName = 'w-1/2 h-1/2 text-[#94A3B8]',
  ...rest
}) {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) {
    return (
      <div className={fallbackClassName} aria-hidden="true">
        <Music className={iconClassName} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setBroken(true)}
      {...rest}
    />
  );
}
